<template>
    <div class="login-body-polished">
        <div class="container">
            <!-- Left Panel - Branding & Admin Info -->
            <div class="left-panel">
                <div class="brand-content">
                    <div class="logo-container">
                        <div class="logo">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <rect width="48" height="48" rx="12" fill="#0071C5" />
                                <path d="M24 12L32 20L24 28L16 20L24 12Z" fill="white" opacity="0.9" />
                                <path d="M24 20L32 28L24 36L16 28L24 20Z" fill="white" opacity="0.6" />
                            </svg>
                        </div>
                        <h1 class="brand-name">INTELVO SUPER ADMIN</h1>
                    </div>

                    <div class="hero-content text-left">
                        <h2 class="hero-title text-left">Enterprise Control & Governance</h2>
                        <p class="hero-description text-left">Manage cross-company configurations, monitor system-wide
                            PDF processing, and oversee user lifecycle management from a centralized console.</p>

                        <div class="features">
                            <!-- Feature 1: Multi-Company -->
                            <div class="feature-item">
                                <div class="feature-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path d="M3 21h18" />
                                        <path
                                            d="M3 7v1a3 3 0 0 0 6 0V7m6 0v1a3 3 0 0 0 6 0V7m-6 0h6M9 7h6M9 7V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4" />
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <div class="block mb-2px" style="font-weight: 600;">Multi-Company Management</div>
                                    <div class="text-sm opacity-90 leading-tight">Authorize organizations, manage
                                        licenses, and control platform access at the enterprise level.</div>
                                </div>
                            </div>

                            <!-- Feature 2: User Governance -->
                            <div class="feature-item">
                                <div class="feature-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <div class="block mb-2px" style="font-weight: 600;">Global User Governance</div>
                                    <div class="text-sm opacity-90 leading-tight">Oversee administrator roles and user
                                        activities across all registered companies.</div>
                                </div>
                            </div>

                            <!-- Feature 3: PDF Audit -->
                            <div class="feature-item">
                                <div class="feature-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <rect x="8" y="13" width="8" height="2" />
                                        <rect x="8" y="17" width="8" height="2" />
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <div class="block mb-2px" style="font-weight: 600;">System-wide Audit Logs</div>
                                    <div class="text-sm opacity-90 leading-tight">Monitor real-time PDF analysis status
                                        and maintain comprehensive audit trails for transparency.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="decorative-elements">
                    <div class="blob blob-1"></div>
                    <div class="blob blob-2"></div>
                    <div class="blob blob-3"></div>
                </div>
            </div>

            <!-- Right Panel - Login Form -->
            <div class="right-panel">
                <div class="login-container">
                    <div class="login-header text-left">
                        <h2 class="login-title text-left">Super Admin Login</h2>
                        <p class="login-subtitle text-left">Authorized access only. Enter your administrator credentials
                            to proceed.</p>
                    </div>

                    <form class="login-form" @submit.prevent="handleLogin">
                        <div class="form-group">
                            <label for="email" class="form-label block text-left">Administrator Email</label>
                            <div class="input-wrapper">
                                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2">
                                    <path
                                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" />
                                    <path d="M22 6L12 13L2 6" />
                                </svg>
                                <input type="email" id="email" class="form-input" placeholder="admin@intelvo.com"
                                    v-model="email" required>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="password" class="form-label block text-left">Security Password</label>
                            <div class="input-wrapper">
                                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path
                                        d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" />
                                </svg>
                                <input type="password" id="password" class="form-input" placeholder="••••••••"
                                    v-model="password" required>
                            </div>
                        </div>

                        <button type="submit" class="btn-primary">
                            <span class="btn-text">SIGN IN</span>
                            <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2">
                                <path d="M5 12H19" />
                                <path d="M12 5L19 12L12 19" />
                            </svg>
                        </button>
                        <p v-if="error" class="text-red mt-2"
                            style="text-align: center; color: #ef4444; font-weight: 600;">{{ error }}</p>
                    </form>

                    <div class="text-center mt-6 text-gray-400" style="font-size: 0.8rem;">
                        Intelvo Super Admin v1.0.4 | Secure Enterprise Access
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SuperAdminLogin',
    data() {
        return {
            email: '',
            password: '',
            error: null
        }
    },
    methods: {
        async handleLogin() {
            this.error = null;
            try {
                const params = new URLSearchParams();
                params.append('username', this.email.trim());
                params.append('password', this.password.trim());

                const response = await axios.post(`${this.$API_URL}/token`, params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });

                const responseData = response.data;

                // CRITICAL SECURITY CHECK: 
                // 1. User MUST belong to a Super Admin company
                // 2. User MUST have the 'admin' role
                const user = responseData.user;
                const isAdmin = user && user.roles && (user.roles.includes('superadmin') || user.roles.includes('admin'));
                const isSuperCompany = user && user.is_super_admin_company;

                if (!isAdmin || !isSuperCompany) {
                    this.error = 'Access restricted.';
                    return;
                }

                localStorage.setItem('super_admin_token', responseData.access_token);
                setTimeout(() => {
                    window.location.href = '/';
                }, 100);
            } catch (err) {
                this.error = 'Invalid credentials or unauthorized access.';
            }
        }
    }
}
</script>

<style scoped>
.login-body-polished {
    height: 100vh;
    overflow: hidden;
}
</style>
